import { useEffect, useState } from 'react';

import styled from 'styled-components';
import moment from 'moment';
import { toast } from 'react-toastify';

import { Typography, Box } from '@mui/material';

// api
import TermApi from '../../api/term.api';
import RoomApi from '../../api/room.api';
import { useAppSelector } from '../../hooks/hooks';

// comps
import GlobalButton from '../../components/global/button';
import GlobalDropdown from '../../components/global/dropdown';
import GlobalInput from '../../components/global/input';

// icons
import { ReactComponent as RefreshIcon } from '../../assets/icons/refresh-icon.svg';

import { colors } from '../../theme';

// constants
import { MOMENT_DATE_FORMAT } from '../../constants/date_format';
import { downloadExcelFile } from '../../helpers/file';
import BuildingApi from '../../api/building.api';

const RoomBookingReport = (props: any) => {
    const _roomApi = new RoomApi();
    const _buildingApi = new BuildingApi();

    const appState = useAppSelector((state) => state.appState);

    const [state, setState] = useState({
        buildings: [],
        roomData: [],
    });

    const [selected, setSelected] = useState({
        // Room Booking props
        startDate: moment().format(MOMENT_DATE_FORMAT),
        endDate: '',
        building: undefined,
        rooms: [],
    });

    useEffect(() => {
        const loadBuildings = async (campusId: number) => {
            const buildings = await _buildingApi.GetAllBuildings(campusId);

            let selectedBuilding = null;

            // //Auto select if there is only one building. The building dropdown will be hidden
            if (buildings.length === 0) {
                selectedBuilding = buildings[-1];
            }

            setState((prevState) => {
                return {
                    ...prevState,
                    buildings,
                    roomData: [],
                };
            });

            setSelected((prevState) => {
                return {
                    startDate: moment().format(MOMENT_DATE_FORMAT),
                    endDate: '',
                    building: selectedBuilding,
                    rooms: [],
                };
            });
        };

        if (appState.campus?.id) {
            loadBuildings(appState.campus.id).catch((error) => toast.error('Error loading data'));
        }
    }, [appState.campus]);

    useEffect(() => {
        const loadRooms = async (buildingId: number) => {
            const roomData = await _roomApi.GetRooms(buildingId);
            setState((prev) => ({ ...prev, roomData }));
        };

        if (selected.building?.id) {
            loadRooms(selected.building.id).catch((error) => toast.error('Error loading data'));
        }
    }, [selected.building]);

    const handleSelection = (name: any, value: any) => {
        if (selected.startDate && name === 'endDate') {
            if (moment(value).isSameOrBefore(selected.startDate)) {
                toast.error('End Date must be AFTER Start Date.');
                return;
            }
        }

        setSelected((prev) => ({ ...prev, [name]: value, rooms: name === 'building' ? [] : prev.rooms }));
    };

    const handleRoomSelect = (event) => {
        const {
            target: { name, value },
        } = event;

        setSelected((prev) => ({ ...prev, [name]: [...value] }));
    };

    const handleRoomRemove = (event) => {
        const rooms = selected.rooms.filter((room) => room.id !== event.id);

        setSelected({
            ...selected,
            rooms,
        });
    };

    const handleClearRoomBooking = () => {
        setSelected({
            ...selected,
            startDate: moment().format(MOMENT_DATE_FORMAT),
            endDate: '',
            building: undefined,
            rooms: [],
        });
    };

    const handleGenerateRoomReport = async () => {
        const { startDate, endDate } = selected;
        const rooms = selected?.rooms.map((item) => item.id);
        const reportData = await _roomApi.ExportRoomBookingReport(rooms, startDate, endDate);
        const fileName = `Room_Bookings_Report_${startDate}-${endDate}`;
        downloadExcelFile(reportData, fileName);
        handleClearRoomBooking();
    };

    console.log('selected', selected);

    return (
        <>
            <Box sx={{ height: '88vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <ReportBox>
                    <FlexBox sx={{ mb: '20px' }}>
                        <Typography variant="h4" color={colors.blue.main} sx={{ mr: '10px' }}>
                            Room Booking Preview
                        </Typography>
                        <Typography variant="body1" color={colors.grey.medium}>
                            Current Bookings
                        </Typography>
                    </FlexBox>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            '& > *': {
                                mb: '10px',
                            },
                        }}
                    >
                        <GlobalInput label="Start Date" placeholder="Start Date" type={'date'} name="startDate" value={selected.startDate} onChange={handleSelection} />
                        <GlobalInput label="end date" placeholder="End Date" type={'date'} name="endDate" value={selected.endDate} onChange={handleSelection} />
                        <GlobalDropdown
                            label="building"
                            placeholder="Select a building"
                            data={state.buildings}
                            value={selected.building}
                            name="name"
                            field="id"
                            dataItemKey={'building'}
                            onChange={handleSelection}
                            isSearch
                        />
                        <GlobalDropdown
                            multiSelect
                            label="room"
                            placeholder="Select room(s)"
                            data={state.roomData}
                            value={selected.rooms}
                            dataItemKey={'rooms'}
                            name={'number'}
                            field={'id'}
                            onChange={handleRoomSelect}
                            handleRemove={handleRoomRemove}
                            isSearch
                        />
                        <FlexBox sx={{ mt: '10px', justifyContent: 'space-between' }}>
                            <GlobalButton variant="outlined" text="reset" endIcon={<RefreshIcon />} onClick={handleClearRoomBooking} />
                            <GlobalButton
                                variant="contained"
                                text="generate report"
                                disabled={!selected.startDate || !selected.endDate || selected.rooms?.length === 0}
                                onClick={handleGenerateRoomReport}
                            />
                        </FlexBox>
                    </Box>
                </ReportBox>
            </Box>
        </>
    );
};

export default RoomBookingReport;

const ReportBox = styled(Box)`
    && {
        background-color: ${colors.white};
        box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.05);
        border-radius: 5px;
        padding: 20px 15px;
        min-width: 345px;
        /* max-width: 350px; */
    }
`;

const FlexBox = styled(Box)`
    && {
        display: flex;
        align-items: center;

        & > * {
            margin-right: 10px;
        }
    }
`;
